import { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DefaultButton, PrimaryButton, Spinner, SpinnerSize, Dialog, DialogType, DialogFooter } from '@fluentui/react'

import { getEmailsByDay, getEmailsForDate, EmailDaySummary, EmailDetail, processEmailQueue, getEmailQueueStatus, QueueStatusResponse, LogEntry, requeueEmail } from '../../api'
import { AppStateContext } from '../../state/AppProvider'

import styles from './EmailHistory.module.css'

type View = 'days' | 'day-detail' | 'type-list' | 'email-detail'
type EmailType = 'candidate' | 'client'

const EmailHistory = () => {
  const navigate = useNavigate()
  const appStateContext = useContext(AppStateContext)
  const [view, setView] = useState<View>('days')
  const [days, setDays] = useState<EmailDaySummary[]>([])
  const [dayEmails, setDayEmails] = useState<EmailDetail[]>([])
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedType, setSelectedType] = useState<EmailType | null>(null)
  const [selectedEmail, setSelectedEmail] = useState<EmailDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [showProcessDialog, setShowProcessDialog] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [queueStatus, setQueueStatus] = useState<QueueStatusResponse | null>(null)
  const [processResult, setProcessResult] = useState<string | null>(null)
  const [logEntries, setLogEntries] = useState<LogEntry[]>([])
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const logEndRef = useRef<HTMLDivElement | null>(null)

  const viewRef = useRef(view)
  const selectedDateRef = useRef(selectedDate)
  viewRef.current = view
  selectedDateRef.current = selectedDate

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [logEntries])

  useEffect(() => {
    loadDays()
    loadQueueStatus().then(status => {
      if (status) {
        const s = status.progress.status
        if (s && s !== 'idle' && s !== 'complete' && !s.startsWith('error')) {
          setProcessing(true)
          startPolling()
        }
      }
    })
    return () => stopPolling()
  }, [])

  useEffect(() => {
    const handler = async () => {
      loadDays()
      loadQueueStatus()
      if (selectedDateRef.current && viewRef.current !== 'days') {
        const data = await getEmailsForDate(selectedDateRef.current)
        setDayEmails(data)
      }
    }
    window.addEventListener('app-data-changed', handler)
    return () => window.removeEventListener('app-data-changed', handler)
  }, [])

  useEffect(() => {
    let context = `User is viewing Email History.`
    if (view === 'days') {
      context += ` ${days.length} days with emails.`
    } else if (view === 'day-detail' && selectedDate) {
      context += ` Viewing emails for ${selectedDate}.`
    } else if (view === 'type-list' && selectedDate && selectedType) {
      context += ` Viewing ${selectedType} emails for ${selectedDate}.`
    } else if (view === 'email-detail' && selectedEmail) {
      context += ` Viewing email detail: "${selectedEmail.subject}" to ${selectedEmail.recipient_email}.`
    }
    appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: context })
    return () => { appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: '' }) }
  }, [view, days.length, selectedDate, selectedType, selectedEmail])

  const loadDays = async () => {
    setLoading(true)
    const data = await getEmailsByDay()
    setDays(data)
    setLoading(false)
  }

  const loadQueueStatus = async () => {
    const status = await getEmailQueueStatus()
    setQueueStatus(status)
    if (status?.log) {
      setLogEntries(status.log)
    }
    return status
  }

  const startPolling = () => {
    stopPolling()
    pollRef.current = setInterval(async () => {
      const status = await loadQueueStatus()
      if (status) {
        if (status.log) {
          setLogEntries(status.log)
        }
        const s = status.progress.status
        if (s === 'complete' || s === 'idle' || s.startsWith('error')) {
          stopPolling()
          setProcessing(false)
          if (s === 'complete') {
            setProcessResult(`Sent ${status.progress.sent}, failed ${status.progress.failed} of ${status.progress.total}`)
          } else if (s.startsWith('error')) {
            setProcessResult(`Error: ${s}`)
          }
          loadDays()
          loadQueueStatus()
        }
      }
    }, 2000)
  }

  const handleProcessQueue = async () => {
    setShowProcessDialog(false)
    setProcessing(true)
    setProcessResult(null)
    setLogEntries([])
    const result = await processEmailQueue()
    if (result) {
      startPolling()
    } else {
      setProcessing(false)
      setProcessResult('Failed to start email processing')
    }
  }

  const handleDayClick = async (date: string) => {
    setSelectedDate(date)
    setLoading(true)
    const data = await getEmailsForDate(date)
    setDayEmails(data)
    setLoading(false)
    setView('day-detail')
  }

  const handleTypeClick = (type: EmailType) => {
    setSelectedType(type)
    setView('type-list')
  }

  const handleEmailClick = (email: EmailDetail) => {
    setSelectedEmail(email)
    setView('email-detail')
  }

  const handleBack = () => {
    if (view === 'email-detail') {
      setSelectedEmail(null)
      setView('type-list')
    } else if (view === 'type-list') {
      setSelectedType(null)
      setView('day-detail')
    } else if (view === 'day-detail') {
      setSelectedDate(null)
      setDayEmails([])
      setView('days')
    } else {
      navigate('/')
    }
  }

  const handleRequeue = async (e: React.MouseEvent, email: EmailDetail) => {
    e.stopPropagation()
    const ok = await requeueEmail(email.emaillogid, email.type)
    if (ok) {
      // Update local state to reflect the requeue
      setDayEmails(prev => prev.map(em =>
        em.emaillogid === email.emaillogid ? { ...em, status: 'queued', error_message: null } : em
      ))
      if (selectedEmail?.emaillogid === email.emaillogid) {
        setSelectedEmail({ ...selectedEmail, status: 'queued', error_message: null })
      }
      loadQueueStatus()
    }
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  }

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'sent': return styles.statusSent
      case 'queued': return styles.statusQueued
      case 'failed': return styles.statusFailed
      case 'sending': return styles.statusSending
      default: return ''
    }
  }

  const formatLogTime = (isoStr: string) => {
    const d = new Date(isoStr)
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  const getLogLevelClass = (level: string) => {
    switch (level) {
      case 'info': return styles.logInfo
      case 'success': return styles.logSuccess
      case 'error': return styles.logError
      case 'warning': return styles.logWarning
      default: return ''
    }
  }

  const candidateEmails = dayEmails.filter(e => e.type === 'candidate')
  const clientEmails = dayEmails.filter(e => e.type === 'client')
  const candidateSent = candidateEmails.filter(e => e.status === 'sent').length
  const clientSent = clientEmails.filter(e => e.status === 'sent').length
  const filteredEmails = selectedType === 'candidate' ? candidateEmails : clientEmails

  const totalQueued = queueStatus ? queueStatus.total_queued : 0

  const renderDaysView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={() => navigate('/')}
          title="Back to chat"
        />
        <h1 className={styles.title}>Email History</h1>
        <div className={styles.headerActions}>
          {processResult && (
            <span className={`${styles.processResult} ${processResult.startsWith('Error') || processResult.startsWith('Failed') ? styles.processResultError : ''}`}>
              {processResult}
            </span>
          )}
          {processing && queueStatus && (
            <span className={styles.processingIndicator}>
              <Spinner size={SpinnerSize.xSmall} />
              {queueStatus.progress.current}/{queueStatus.progress.total}
            </span>
          )}
          {totalQueued > 0 && !processing && (
            <PrimaryButton
              className={styles.processButton}
              text={`Process Queue (${totalQueued})`}
              iconProps={{ iconName: 'Send' }}
              onClick={() => { setProcessResult(null); setShowProcessDialog(true) }}
            />
          )}
        </div>
      </div>
      <Dialog
        hidden={!showProcessDialog}
        onDismiss={() => setShowProcessDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Process Email Queue',
          subText: `Are you sure you want to send ${totalQueued} queued email${totalQueued !== 1 ? 's' : ''}?`
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleProcessQueue} text="Send" />
          <DefaultButton onClick={() => setShowProcessDialog(false)} text="Cancel" />
        </DialogFooter>
      </Dialog>
      {(processing || logEntries.length > 0) && (
        <div className={styles.activityLog}>
          <div className={styles.activityLogHeader}>
            <span className={styles.activityLogTitle}>Activity Log</span>
            {!processing && logEntries.length > 0 && (
              <button className={styles.activityLogClear} onClick={() => setLogEntries([])}>Clear</button>
            )}
          </div>
          <div className={styles.activityLogBody}>
            {logEntries.map((entry, i) => (
              <div key={i} className={styles.logEntry}>
                <span className={styles.logTime}>{formatLogTime(entry.time)}</span>
                <span className={getLogLevelClass(entry.level)}>{entry.message}</span>
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      )}
      {loading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading email history..." />
        </div>
      ) : days.length === 0 ? (
        <div className={styles.emptyState}>No emails found.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Candidate Emails</th>
              <th>Client Emails</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {days.map(day => (
              <tr key={day.date} className={styles.clickableRow} onClick={() => handleDayClick(day.date)}>
                <td>{formatDate(day.date)}</td>
                <td>
                  <span className={`${styles.badge} ${styles.badgeCandidate}`}>{day.candidate_count}</span>
                </td>
                <td>
                  <span className={`${styles.badge} ${styles.badgeClient}`}>{day.client_count}</span>
                </td>
                <td>{day.candidate_count + day.client_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )

  const renderDayDetailView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={handleBack}
          title="Back to day list"
        />
        <h1 className={styles.title}>{selectedDate ? formatDate(selectedDate) : ''}</h1>
      </div>
      {loading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading emails..." />
        </div>
      ) : (
        <div className={styles.typeLinks}>
          <div
            className={`${styles.typeCard} ${styles.typeCardCandidate}`}
            onClick={() => candidateEmails.length > 0 && handleTypeClick('candidate')}
            style={{ cursor: candidateEmails.length > 0 ? 'pointer' : 'default', opacity: candidateEmails.length > 0 ? 1 : 0.5 }}
          >
            <div className={styles.typeCardIcon}>
              <span className={`${styles.badge} ${styles.badgeCandidate}`}>Candidates</span>
            </div>
            <div className={styles.typeCardCount}>{candidateSent} <span className={styles.typeCardTotal}>/ {candidateEmails.length}</span></div>
            <div className={styles.typeCardLabel}>sent out of {candidateEmails.length} email{candidateEmails.length !== 1 ? 's' : ''}</div>
          </div>
          <div
            className={`${styles.typeCard} ${styles.typeCardClient}`}
            onClick={() => clientEmails.length > 0 && handleTypeClick('client')}
            style={{ cursor: clientEmails.length > 0 ? 'pointer' : 'default', opacity: clientEmails.length > 0 ? 1 : 0.5 }}
          >
            <div className={styles.typeCardIcon}>
              <span className={`${styles.badge} ${styles.badgeClient}`}>Clients</span>
            </div>
            <div className={styles.typeCardCount}>{clientSent} <span className={styles.typeCardTotal}>/ {clientEmails.length}</span></div>
            <div className={styles.typeCardLabel}>sent out of {clientEmails.length} email{clientEmails.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
      )}
    </>
  )

  const renderTypeListView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={handleBack}
          title="Back to day overview"
        />
        <h1 className={styles.title}>{selectedDate ? formatDate(selectedDate) : ''}</h1>
        <span className={styles.subtitle}>
          <span className={`${styles.badge} ${selectedType === 'candidate' ? styles.badgeCandidate : styles.badgeClient}`}>
            {selectedType === 'candidate' ? 'Candidate' : 'Client'} Emails
          </span>
          &nbsp;({filteredEmails.length})
        </span>
      </div>
      {filteredEmails.length === 0 ? (
        <div className={styles.emptyState}>No {selectedType} emails for this date.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Time</th>
              <th>Recipient</th>
              <th>Subject</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmails.map(email => (
              <tr key={email.emaillogid} className={styles.clickableRow} onClick={() => handleEmailClick(email)}>
                <td>{formatTime(email.createdate)}</td>
                <td>{email.recipient_email}</td>
                <td>{email.subject}</td>
                <td>
                  <span className={getStatusClass(email.status)}>{email.status}</span>
                  {email.retry_count > 0 && (
                    <span className={styles.retryBadge}>retry {email.retry_count}</span>
                  )}
                  {email.error_message && (
                    <div className={styles.errorHint} title={email.error_message}>
                      {email.error_message.length > 40 ? email.error_message.substring(0, 40) + '...' : email.error_message}
                    </div>
                  )}
                  {(email.status === 'failed' || email.status === 'sending') && (
                    <button className={styles.requeueButton} onClick={(e) => handleRequeue(e, email)}>Requeue</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )

  const renderEmailDetailView = () => {
    if (!selectedEmail) return null
    return (
      <>
        <div className={styles.header}>
          <DefaultButton
            className={styles.backButton}
            iconProps={{ iconName: 'Back' }}
            onClick={handleBack}
            title="Back to email list"
          />
          <h1 className={styles.title}>Email Detail</h1>
        </div>
        <div className={styles.emailDetail}>
          <h2 className={styles.emailSubject}>{selectedEmail.subject}</h2>
          <div className={styles.emailMeta}>
            <div className={styles.emailMetaRow}>
              <span className={styles.emailMetaLabel}>From:</span>
              <span className={styles.emailMetaValue}>{selectedEmail.sender_email}</span>
            </div>
            <div className={styles.emailMetaRow}>
              <span className={styles.emailMetaLabel}>To:</span>
              <span className={styles.emailMetaValue}>{selectedEmail.recipient_email}</span>
            </div>
            <div className={styles.emailMetaRow}>
              <span className={styles.emailMetaLabel}>Type:</span>
              <span className={styles.emailMetaValue}>
                <span className={`${styles.badge} ${selectedEmail.type === 'candidate' ? styles.badgeCandidate : styles.badgeClient}`}>
                  {selectedEmail.type}
                </span>
              </span>
            </div>
            <div className={styles.emailMetaRow}>
              <span className={styles.emailMetaLabel}>Status:</span>
              <span className={`${styles.emailMetaValue} ${getStatusClass(selectedEmail.status)}`}>
                {selectedEmail.status}
                {selectedEmail.retry_count > 0 && (
                  <span className={styles.retryBadge}>retry {selectedEmail.retry_count}</span>
                )}
              </span>
            </div>
            {selectedEmail.error_message && (
              <div className={styles.emailMetaRow}>
                <span className={styles.emailMetaLabel}>Error:</span>
                <span className={`${styles.emailMetaValue} ${styles.statusFailed}`}>{selectedEmail.error_message}</span>
              </div>
            )}
            {(selectedEmail.status === 'failed' || selectedEmail.status === 'sending') && (
              <div className={styles.emailMetaRow}>
                <span className={styles.emailMetaLabel}></span>
                <span className={styles.emailMetaValue}>
                  <DefaultButton
                    text="Requeue"
                    iconProps={{ iconName: 'Refresh' }}
                    onClick={(e) => handleRequeue(e as any, selectedEmail)}
                    className={styles.requeueButtonDetail}
                  />
                </span>
              </div>
            )}
            <div className={styles.emailMetaRow}>
              <span className={styles.emailMetaLabel}>Sent:</span>
              <span className={styles.emailMetaValue}>{new Date(selectedEmail.createdate).toLocaleString()}</span>
            </div>
          </div>
          {selectedEmail.body_html ? (
            <div className={styles.emailBody} dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
          ) : selectedEmail.body_plain ? (
            <div className={styles.emailBody}>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>{selectedEmail.body_plain}</pre>
            </div>
          ) : (
            <div className={styles.emptyState}>No email body available.</div>
          )}
        </div>
      </>
    )
  }

  return (
    <div className={styles.container}>
      {view === 'days' && renderDaysView()}
      {view === 'day-detail' && renderDayDetailView()}
      {view === 'type-list' && renderTypeListView()}
      {view === 'email-detail' && renderEmailDetailView()}
    </div>
  )
}

export default EmailHistory
